"use client";

/**
 * The account-security settings sections: two-factor authentication, password
 * change, device sessions, personal API keys and outbound webhooks. They ride
 * the hub's `/api/auth/*` (securityApi) and the drive's `/keys` + `/webhooks`
 * (driveClient) — the same doors the rest of the app uses.
 */

import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { CheckIcon, DocumentDuplicateIcon, TrashIcon } from "@heroicons/react/24/outline";

import { SettingGroup, SettingRow } from "@suite/settings";
import { appPrompt } from "@/lib/dialogs";
import { driveClient } from "@/lib/drive/driveClient";
import type { ApiKeyCreated, ApiKeyRow, WebhookCreated, WebhookRow } from "@/lib/drive/driveClient";
import {
  changePassword,
  disableTwoFactor,
  enableTwoFactor,
  listSessions,
  revokeOtherSessions,
  revokeSession,
  setupTwoFactor,
  type SessionRow,
} from "@/lib/securityApi";
import { formatTimestamp } from "@/lib/drive/format";

/* --------------------------------------------------------------- two factor */

export function SecuritySection() {
  const { t } = useTranslation();
  const [twoFactorBusy, setTwoFactorBusy] = useState(false);
  const [otpauth, setOtpauth] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);

  const startSetup = async () => {
    setTwoFactorBusy(true);
    try {
      const setup = await setupTwoFactor();
      setSecret(setup.secret);
      setOtpauth(setup.otpauth);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    } finally {
      setTwoFactorBusy(false);
    }
  };

  const confirmEnable = async () => {
    const code = await appPrompt(t("settings.twoFactorCodePrompt"), "", {
      title: t("settings.twoFactorEnable"),
      confirmLabel: t("common.save"),
      cancelLabel: t("common.cancel"),
    });
    if (!code) return;
    setTwoFactorBusy(true);
    try {
      const result = await enableTwoFactor(code.trim());
      setRecoveryCodes(result.recoveryCodes ?? []);
      setOtpauth(null);
      setSecret(null);
      toast.success(t("settings.twoFactorEnabled"));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    } finally {
      setTwoFactorBusy(false);
    }
  };

  const confirmDisable = async () => {
    const code = await appPrompt(t("settings.twoFactorCodePrompt"), "", {
      title: t("settings.twoFactorDisable"),
      confirmLabel: t("common.save"),
      cancelLabel: t("common.cancel"),
    });
    if (!code) return;
    setTwoFactorBusy(true);
    try {
      await disableTwoFactor(code.trim());
      setRecoveryCodes(null);
      toast.success(t("settings.twoFactorDisabled"));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    } finally {
      setTwoFactorBusy(false);
    }
  };

  const copySecret = async () => {
    if (!secret) return;
    await navigator.clipboard.writeText(secret).catch(() => undefined);
    toast.success(t("settings.copied"));
  };

  return (
    <SettingGroup title={t("settings.security")}>
      <SettingRow
        label={t("settings.twoFactor")}
        hint={t("settings.twoFactorHint")}
        control={
          otpauth ? (
            <button
              type="button"
              onClick={() => void confirmEnable()}
              disabled={twoFactorBusy}
              className="whitespace-nowrap rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-background transition-opacity hover:opacity-90 disabled:opacity-50"
            >
              {t("settings.twoFactorEnterCode")}
            </button>
          ) : recoveryCodes ? (
            <span className="whitespace-nowrap text-sm text-success">{t("settings.twoFactorOn")}</span>
          ) : (
            <button
              type="button"
              onClick={() => void startSetup()}
              disabled={twoFactorBusy}
              className="whitespace-nowrap rounded-md border border-border-color px-3 py-1.5 text-sm text-foreground transition-colors hover:bg-muted disabled:opacity-50"
            >
              {t("settings.twoFactorSetup")}
            </button>
          )
        }
      />
      {otpauth && (
        <div className="flex flex-nowrap items-center gap-2 rounded-md bg-muted px-3 py-2">
          <code className="min-w-0 flex-1 truncate text-xs text-foreground" title={otpauth}>
            {otpauth}
          </code>
          <button
            type="button"
            onClick={() => void copySecret()}
            title={t("settings.copied")}
            className="flex-shrink-0 rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-background hover:text-foreground"
          >
            <DocumentDuplicateIcon className="h-4 w-4" />
          </button>
        </div>
      )}
      {recoveryCodes && (
        <div className="rounded-md bg-muted px-3 py-2">
          <p className="whitespace-nowrap text-xs font-medium text-foreground">{t("settings.recoveryCodes")}</p>
          <div className="mt-1 grid grid-cols-2 gap-1 sm:grid-cols-4">
            {recoveryCodes.map((code) => (
              <code key={code} className="whitespace-nowrap text-xs text-foreground">
                {code}
              </code>
            ))}
          </div>
          <p className="mt-1 text-xs text-muted-foreground">{t("settings.recoveryCodesHint")}</p>
        </div>
      )}
      {!otpauth && recoveryCodes && (
        <SettingRow
          label=""
          control={
            <button
              type="button"
              onClick={() => void confirmDisable()}
              className="whitespace-nowrap rounded-md border border-border-color px-3 py-1.5 text-sm text-destructive transition-colors hover:bg-muted"
            >
              {t("settings.twoFactorDisable")}
            </button>
          }
        />
      )}
      <PasswordRow />
      <SessionsSection />
    </SettingGroup>
  );
}

function PasswordRow() {
  const { t } = useTranslation();
  const change = async () => {
    const currentPassword = await appPrompt(t("settings.currentPassword"), "", {
      title: t("settings.changePassword"),
      confirmLabel: t("common.next"),
      cancelLabel: t("common.cancel"),
    });
    if (!currentPassword) return;
    const newPassword = await appPrompt(t("settings.newPassword"), "", {
      title: t("settings.changePassword"),
      confirmLabel: t("common.save"),
      cancelLabel: t("common.cancel"),
    });
    if (!newPassword) return;
    try {
      await changePassword(currentPassword, newPassword);
      toast.success(t("settings.passwordChanged"));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <SettingRow
      label={t("settings.changePassword")}
      hint={t("settings.changePasswordHint")}
      control={
        <button
          type="button"
          onClick={() => void change()}
          className="whitespace-nowrap rounded-md border border-border-color px-3 py-1.5 text-sm text-foreground transition-colors hover:bg-muted"
        >
          {t("common.edit")}
        </button>
      }
    />
  );
}

function SessionsSection() {
  const { t, i18n } = useTranslation();
  const [sessions, setSessions] = useState<SessionRow[] | null>(null);

  const load = useCallback(async () => {
    try {
      setSessions(await listSessions());
    } catch {
      setSessions([]);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const revokeOne = async (id: string) => {
    try {
      await revokeSession(id);
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    }
  };

  const revokeOthers = async () => {
    try {
      await revokeOtherSessions();
      toast.success(t("settings.sessionsRevoked"));
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <div className="flex flex-col gap-2 border-t border-border-color pt-3">
      <div className="flex flex-nowrap items-center justify-between gap-2">
        <p className="whitespace-nowrap text-sm font-medium text-foreground">{t("settings.sessions")}</p>
        <button
          type="button"
          onClick={() => void revokeOthers()}
          className="flex-shrink-0 whitespace-nowrap rounded-md px-2 py-1 text-xs text-destructive transition-colors hover:bg-muted"
        >
          {t("settings.revokeOthers")}
        </button>
      </div>
      {sessions === null ? (
        <p className="text-xs text-muted-foreground">…</p>
      ) : (
        <ul className="flex flex-col gap-1">
          {sessions
            .filter((session) => !session.revoked)
            .map((session) => (
              <li
                key={session.id}
                className="flex flex-nowrap items-center gap-2 rounded-md border border-border-color px-2.5 py-1.5"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate whitespace-nowrap text-xs text-foreground" title={session.userAgent}>
                    {session.userAgent || t("settings.unknownDevice")}
                    {session.current ? ` · ${t("settings.currentDevice")}` : ""}
                  </p>
                  <p className="whitespace-nowrap text-[10px] text-muted-foreground">
                    {formatTimestamp(new Date(session.lastSeenAt).getTime(), i18n.resolvedLanguage)} · {session.ip}
                  </p>
                </div>
                {!session.current && (
                  <button
                    type="button"
                    onClick={() => void revokeOne(session.id)}
                    className="flex-shrink-0 whitespace-nowrap rounded-md px-2 py-1 text-xs text-destructive transition-colors hover:bg-muted"
                  >
                    {t("settings.revoke")}
                  </button>
                )}
              </li>
            ))}
        </ul>
      )}
    </div>
  );
}

/* --------------------------------------------------------- API keys, webhooks */

export function ApiAccessSection() {
  const { t } = useTranslation();
  return (
    <SettingGroup title={t("settings.apiAccess")}>
      <ApiKeysSection />
      <WebhooksSection />
    </SettingGroup>
  );
}

function ApiKeysSection() {
  const { t } = useTranslation();
  const [keys, setKeys] = useState<ApiKeyRow[]>([]);
  const [created, setCreated] = useState<ApiKeyCreated | null>(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    try {
      setKeys(await driveClient.listApiKeys());
    } catch {
      setKeys([]);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const create = async () => {
    const name = await appPrompt(t("settings.keyName"), "", {
      title: t("settings.newApiKey"),
      confirmLabel: t("common.save"),
      cancelLabel: t("common.cancel"),
    });
    if (!name) return;
    try {
      const minted = await driveClient.createApiKey(name, false);
      setCreated(minted);
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    }
  };

  const revoke = async (id: string) => {
    try {
      await driveClient.revokeApiKey(id);
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    }
  };

  const copyKey = async () => {
    if (!created) return;
    await navigator.clipboard.writeText(created.key).catch(() => undefined);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="flex flex-col gap-2 border-t border-border-color pt-3">
      <div className="flex flex-nowrap items-center justify-between gap-2">
        <p className="whitespace-nowrap text-sm font-medium text-foreground">{t("settings.apiKeys")}</p>
        <button
          type="button"
          onClick={() => void create()}
          className="flex-shrink-0 whitespace-nowrap rounded-md border border-border-color px-2 py-1 text-xs text-foreground transition-colors hover:bg-muted"
        >
          {t("settings.newApiKey")}
        </button>
      </div>
      {created && (
        <div className="flex flex-nowrap items-center gap-2 rounded-md bg-muted px-2.5 py-2">
          <code className="min-w-0 flex-1 truncate text-xs text-foreground">{created.key}</code>
          <button
            type="button"
            onClick={() => void copyKey()}
            title={t("drive.copy")}
            className="flex-shrink-0 rounded-md p-1 text-muted-foreground hover:text-foreground"
          >
            {copied ? <CheckIcon className="h-4 w-4 text-success" /> : <DocumentDuplicateIcon className="h-4 w-4" />}
          </button>
        </div>
      )}
      <ul className="flex flex-col gap-1">
        {keys
          .filter((key) => !key.revokedAt)
          .map((key) => (
            <li
              key={key.id}
              className="flex flex-nowrap items-center gap-2 rounded-md border border-border-color px-2.5 py-1.5"
            >
              <div className="min-w-0 flex-1">
                <p className="truncate whitespace-nowrap text-xs text-foreground">{key.name}</p>
                <p className="whitespace-nowrap text-[10px] text-muted-foreground">
                  {key.keyPrefix}… · {key.scopes}
                </p>
              </div>
              <button
                type="button"
                onClick={() => void revoke(key.id)}
                className="flex-shrink-0 rounded-md p-1 text-destructive transition-colors hover:bg-muted"
              >
                <TrashIcon className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
      </ul>
    </div>
  );
}

function WebhooksSection() {
  const { t } = useTranslation();
  const [hooks, setHooks] = useState<WebhookRow[]>([]);
  const [created, setCreated] = useState<WebhookCreated | null>(null);

  const load = useCallback(async () => {
    try {
      setHooks(await driveClient.listWebhooks());
    } catch {
      setHooks([]);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const create = async () => {
    const url = await appPrompt(t("settings.webhookUrl"), "https://", {
      title: t("settings.newWebhook"),
      confirmLabel: t("common.save"),
      cancelLabel: t("common.cancel"),
    });
    if (!url || !/^https?:\/\//i.test(url.trim())) return;
    try {
      const hook = await driveClient.createWebhook(url.trim(), ["*"]);
      setCreated(hook);
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    }
  };

  const remove = async (id: string) => {
    try {
      await driveClient.deleteWebhook(id);
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <div className="flex flex-col gap-2 border-t border-border-color pt-3">
      <div className="flex flex-nowrap items-center justify-between gap-2">
        <p className="whitespace-nowrap text-sm font-medium text-foreground">{t("settings.webhooks")}</p>
        <button
          type="button"
          onClick={() => void create()}
          className="flex-shrink-0 whitespace-nowrap rounded-md border border-border-color px-2 py-1 text-xs text-foreground transition-colors hover:bg-muted"
        >
          {t("settings.newWebhook")}
        </button>
      </div>
      {created && (
        <div className="rounded-md bg-muted px-2.5 py-2">
          <p className="whitespace-nowrap text-xs font-medium text-foreground">{t("settings.webhookSecret")}</p>
          <code className="block truncate text-xs text-foreground">{created.secret}</code>
        </div>
      )}
      <ul className="flex flex-col gap-1">
        {hooks.map((hook) => (
          <li
            key={hook.id}
            className="flex flex-nowrap items-center gap-2 rounded-md border border-border-color px-2.5 py-1.5"
          >
            <div className="min-w-0 flex-1">
              <p className="truncate whitespace-nowrap text-xs text-foreground" title={hook.url}>
                {hook.url}
              </p>
              <p className="whitespace-nowrap text-[10px] text-muted-foreground">
                {hook.events}
                {hook.active ? "" : ` · ${t("settings.webhookDisabled")}`}
              </p>
            </div>
            <button
              type="button"
              onClick={() => void remove(hook.id)}
              className="flex-shrink-0 rounded-md p-1 text-destructive transition-colors hover:bg-muted"
            >
              <TrashIcon className="h-3.5 w-3.5" />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ------------------------------------------------------------------ billing */

/**
 * The billing section moved to `BillingContent.tsx` (Plan 02 Phase 3): the
 * shared kit's snapshot-driven storage meters replaced the retired legacy
 * quota-tier list. Re-exported here so the existing settings imports keep
 * their shape.
 */
export { BillingSection } from "./BillingContent";
